// Derives every chart/table dataset from the raw /api/admin/registrations
// response. Kept separate so the page component stays focused on layout.
import { useMemo } from "react";
import { eventConfig } from "@/config/eventConfig";
import { DIRECT_LABEL, emptyChannels, normCode, resolveAdviserLabel } from "./dashboardUtils";

function filterItems(items, query) {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((it) =>
    [it.full_name, it.email, it.mobile, it.invited_by]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q))
  );
}

// Aggregate sign-ups per adviser (merging aliases into one label).
function buildAdviserChartData(byReferral) {
  const merged = {};
  Object.entries(byReferral).forEach(([key, count]) => {
    const label = resolveAdviserLabel(key);
    merged[label] = (merged[label] || 0) + count;
  });
  return Object.entries(merged)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

// Registrations tallied by normalised code (Direct rolled into "DIRECT").
function buildRegByCode(byReferral) {
  const out = {};
  Object.entries(byReferral).forEach(([key, count]) => {
    let code = normCode(key);
    if (!code || code === "DIRECT / NONE") code = "DIRECT";
    out[code] = (out[code] || 0) + count;
  });
  return out;
}

// One row per adviser link (+ any extra codes) with clicks/unique/regs/conv,
// and a "Direct" row pinned to the bottom.
function buildTrafficRows(traffic, regByCode) {
  const seen = new Set();
  const rows = [];
  const makeRow = (code, name) => {
    const key = normCode(code);
    const t = traffic[key] || { clicks: 0, unique: 0, channels: emptyChannels() };
    const regs = regByCode[key] || 0;
    const conv = t.unique > 0 ? (regs / t.unique) * 100 : 0;
    return {
      code: key,
      name,
      clicks: t.clicks || 0,
      unique: t.unique || 0,
      channels: { ...emptyChannels(), ...(t.channels || {}) },
      regs,
      conv,
    };
  };
  (eventConfig.advisers || []).forEach((a) => {
    seen.add(normCode(a.code));
    rows.push(makeRow(a.code, a.name));
  });
  new Set([...Object.keys(traffic), ...Object.keys(regByCode)]).forEach((code) => {
    const key = normCode(code);
    if (key === "DIRECT" || seen.has(key)) return;
    seen.add(key);
    rows.push(makeRow(key, resolveAdviserLabel(key)));
  });
  rows.sort((a, b) => b.clicks - a.clicks || b.regs - a.regs);
  rows.push(makeRow("DIRECT", "Direct / no link"));
  return rows;
}

function sumTraffic(rows) {
  const t = { clicks: 0, unique: 0, regs: 0 };
  rows.forEach((r) => {
    t.clicks += r.clicks;
    t.unique += r.unique;
    t.regs += r.regs;
  });
  t.conv = t.unique > 0 ? (t.regs / t.unique) * 100 : 0;
  return t;
}

// Best performing adviser link: highest conversion (min 1 unique visitor),
// tie-break on sign-ups then unique visitors. Excludes the Direct row.
function pickBestPerformer(rows) {
  const candidates = rows.filter((r) => r.code !== "DIRECT" && r.unique > 0 && r.regs > 0);
  if (!candidates.length) return null;
  return [...candidates].sort((a, b) => b.conv - a.conv || b.regs - a.regs || b.unique - a.unique)[0];
}

// Daily clicks trend for the line chart (fills gaps between first/last day).
function buildDailyTrend(raw) {
  if (!raw.length) return [];
  const byDate = {};
  raw.forEach((d) => (byDate[d.date] = d));
  const start = new Date(raw[0].date + "T00:00:00");
  const end = new Date(raw[raw.length - 1].date + "T00:00:00");
  const out = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const iso = d.toISOString().slice(0, 10);
    const hit = byDate[iso];
    out.push({
      date: iso,
      label: d.toLocaleDateString("en-SG", { day: "numeric", month: "short" }),
      clicks: hit ? hit.clicks : 0,
      unique: hit ? hit.unique : 0,
    });
  }
  return out;
}

// Stable fallbacks so memo dependencies don't change identity on every render.
const EMPTY_LIST = Object.freeze([]);
const EMPTY_MAP = Object.freeze({});

export function useDashboardMetrics(data, query) {
  const items = data?.items || EMPTY_LIST;
  const byReferral = data?.by_referral || EMPTY_MAP;
  const traffic = data?.traffic || EMPTY_MAP;
  const trafficDaily = data?.traffic_daily || EMPTY_LIST;

  const filteredItems = useMemo(() => filterItems(items, query), [items, query]);
  const consentCount = items.filter((i) => i.marketing_consent).length;

  const adviserChartData = useMemo(() => buildAdviserChartData(byReferral), [byReferral]);
  const leaderboard = useMemo(
    () => adviserChartData.filter((d) => d.name !== DIRECT_LABEL),
    [adviserChartData]
  );
  const topAdvisers = leaderboard.slice(0, 12);

  const regByCode = useMemo(() => buildRegByCode(byReferral), [byReferral]);
  const trafficRows = useMemo(() => buildTrafficRows(traffic, regByCode), [traffic, regByCode]);
  const trafficTotals = useMemo(() => sumTraffic(trafficRows), [trafficRows]);
  const activeTrafficRows = useMemo(
    () => trafficRows.filter((r) => r.clicks > 0 || r.unique > 0 || r.regs > 0),
    [trafficRows]
  );
  const bestPerformer = useMemo(() => pickBestPerformer(trafficRows), [trafficRows]);
  const dailyTrend = useMemo(() => buildDailyTrend(trafficDaily), [trafficDaily]);

  const referralCount = (code) => byReferral[code] || 0;

  return {
    total: data?.count || 0,
    filteredItems,
    consentCount,
    topAdvisers,
    leaderboard,
    trafficTotals,
    activeTrafficRows,
    bestPerformer,
    dailyTrend,
    referralCount,
  };
}
